import BrandMark from "../components/brand/BrandMark";
import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, FileText, Users, Mail, BookOpen } from "lucide-react";
import ReactMarkdown from "react-markdown";

const API = '/api';

const sectionConfig = {
  "/terminos": {
    key: "terms_conditions",
    title: "Términos y Condiciones",
    icon: FileText,
    color: "text-blue-500"
  },
  "/privacidad": {
    key: "privacy_policy",
    title: "Política de Privacidad",
    icon: FileText,
    color: "text-indigo-500"
  },
  "/sobre-nosotros": {
    key: "about_us", 
    title: "Sobre Nosotros",
    icon: Users,
    color: "text-[#1B84FF]"
  },
  "/contacto": {
    key: "contact_us",
    title: "Contáctenos",
    icon: Mail,
    color: "text-purple-500"
  },
  "/reclamaciones": {
    key: "complaints_book",
    title: "Libro de Reclamaciones",
    icon: BookOpen,
    color: "text-red-500"
  }
};

// Markdown component overrides - defined outside component to avoid recreation
const markdownComponents = {
  h1: (props) => <h1 className="text-2xl font-bold text-slate-900 mb-4" {...props} />,
  h2: (props) => <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4" {...props} />,
  h3: (props) => <h3 className="text-lg font-semibold text-slate-700 mt-6 mb-3" {...props} />,
  p: (props) => <p className="text-slate-600 mb-4 leading-relaxed" {...props} />,
  ul: (props) => <ul className="list-disc list-inside text-slate-600 mb-4 space-y-2" {...props} />,
  ol: (props) => <ol className="list-decimal list-inside text-slate-600 mb-4 space-y-2" {...props} />,
  li: (props) => <li className="text-slate-600" {...props} />,
  strong: (props) => <strong className="text-slate-900 font-semibold" {...props} />,
};

const LegalPage = () => {
  const location = useLocation();
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);

  const config = sectionConfig[location.pathname];

  const fetchContent = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/site-content/${config.key}`);
      setContent(response.data.content);
    } catch (error) {
      setContent("Error al cargar el contenido");
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  if (!config) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Página no encontrada</h1>
            <Link to="/">
              <Button>Volver al inicio</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandMark size="sm" asLink={false} />
          </Link>
          <Link to="/">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center ${config.color}`}>
              <Icon className="w-6 h-6" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {config.title}
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B84FF]"></div>
          </div>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="p-8 prose prose-slate max-w-none">
              <ReactMarkdown components={markdownComponents}>
                {content}
              </ReactMarkdown>
            </CardContent>
          </Card>
        )}

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-slate-200">
          <h3 className="text-sm font-semibold text-slate-500 mb-4">Otras páginas</h3>
          <div className="flex flex-wrap gap-4">
            {Object.entries(sectionConfig).map(([path, cfg]) => (
              path !== location.pathname && (
                <Link key={path} to={path}>
                  <Button variant="outline" size="sm" className="text-slate-600">
                    {cfg.title}
                  </Button>
                </Link>
              )
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            © 2026 AvisoCar. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
